import { v4 as uuidv4 } from "uuid";
import { ActionTypeDefinition } from "../action_type";

import {
  ConditionInputDefinition,
  BranchComparison,
  JourneyContext,
  BranchCondition,
  Action,
  Journey,
  ActionGuid,
  JourneyTemplate,
  JourneyGuid,
} from "../types";
import { error, bug } from "../../lib/error";
import { formatDate } from "@flow/lib/formatters";
import { mergeDeepRight, clone, uniq } from "ramda";

/**
 * Represent types of errors - only one at the moment but still useful to have type
 * for runtime checking.
 */
export enum EditJourneyErrorTypes {
  NotAllowed = "NotAllowed",
}
/**
 * represent an error
 */
export interface EditJourneyError {
  type: EditJourneyErrorTypes;
  message?: string;
}

/**
 * create a EditJourneyError with optional error message
 */
function createError(message?: string): EditJourneyError {
  return { type: EditJourneyErrorTypes.NotAllowed, message: message };
}

/**
 * Typeguard , return true if j is an error
 */
export function isEditJourneyError(j: any): j is EditJourneyError {
  return j?.type === EditJourneyErrorTypes.NotAllowed;
}

/**
 *
 * @returns an empty JourneyContext
 */
export function createContext(): JourneyContext {
  return { seed: 0, utm: {}, member: {}, actionData: {} };
}

/**
 *  Create a new, almost empty journey.
 * @param slug journey slug
 * @param name journey name
 * @returns new Journey
 */
export function createJourney(show_journey_title = true, slug?: string, name?: string, username?: string): Journey {
  const uuid = uuidv4();
  return {
    slug: slug || uuid,
    name: name || "<title>",
    internal_name: undefined,
    guid: uuid,
    created_at: new Date(),
    updated_at: new Date(),
    actions: [],
    went_live_at: undefined,
    og_image: "https://files.38degrees.org.uk/items/files/000/003/413/original/shutterstock_663459544.jpg",
    og_description: "I just signed the campaign calling to save XYZ",
    og_title: "",
    initial_action_guid: undefined,
    created_by: username || "Admin",
    show_journey_title: show_journey_title,
    show_on_public_lists: true,
    campaigns: undefined,
  };
}

/**
 * Create a new journey using template, and the sourceJourney template refers to.
 * Both are required so we don't have to concern ourselves with calling the backend to load journey from guid.
 * Actions are copied and updated with new action guids , including the branches.
 */
export function createJourneyFromTemplate(
  template: JourneyTemplate,
  sourceJourney: Journey,
  newJourneyGuid: JourneyGuid,
  username?: string
): Journey {
  const guidMap: { [oldGuid: string]: string } = {};

  // Generate new GUIDs for all actions and map old GUIDs to new GUIDs
  for (const action of sourceJourney.actions) {
    guidMap[action.guid] = uuidv4();
  }

  // Clone actions with new GUIDs and update target_guids in branches
  const newActions: Action[] = sourceJourney.actions.map((oldAction) => {
    return {
      ...oldAction,
      guid: guidMap[oldAction.guid],
      journey_guid: newJourneyGuid,
      // For each branch of the old action, we map the old target_guid to the new one
      branches: oldAction.branches.map((branch) => ({
        comparison: branch.comparison,
        target_guid: guidMap[branch.target_guid],
      })),
    };
  });

  const now = new Date();

  return {
    ...sourceJourney,
    guid: newJourneyGuid,
    slug: sourceJourney.slug.replace("${date}", formatDate(now)),
    internal_name: sourceJourney.internal_name?.replace("${date}", formatDate(now)),
    created_at: now,
    updated_at: now,
    created_by: username || "Admin",
    actions: newActions,
    went_live_at: undefined,
    initial_action_guid: sourceJourney.initial_action_guid ? guidMap[sourceJourney.initial_action_guid] : undefined,
    show_journey_title: sourceJourney.show_journey_title,
    campaigns: undefined,
  };
}

/**
 * Given an ActionTypeDefinition and an array of action configs (that match that action type)
 * return an array of AdminActionConfigs.
 * Allows type safe handling of action configs variatiants.
 */
export function readAllTypedActionConfigs<T_AdminActionConfig, T_PublicActionConfig>(
  def: ActionTypeDefinition<T_AdminActionConfig, T_PublicActionConfig>,
  config: any[]
): T_AdminActionConfig[] {
  return config.map((c) => readOneTypedActionConfig(def, c));
}

/**
 * Unserialize one variant in an action.config array.
 * single_config should be one of action.config[n]
 */
export function readOneTypedActionConfig<T_AdminActionConfig, T_PublicActionConfig>(
  def: ActionTypeDefinition<T_AdminActionConfig, T_PublicActionConfig>,
  single_config: any
): T_AdminActionConfig {
  // fill in missing custom_config keys with defaults. this makes adding features
  // to existing action types less likely to break old journeys
  const raw_config_with_defaults = { ...def.custom_config_type.write(def.custom_config_defaults), ...single_config };
  return def.custom_config_type.read(raw_config_with_defaults);
}

export function createAction<T_admin, T_public>(
  typeDefinition: null | ActionTypeDefinition<T_admin, T_public>
): Action {
  return {
    journey_guid: "",
    type: typeDefinition ? typeDefinition.action_type : null,
    // start with one config variant, taking default property values from typeDefinition
    config: [typeDefinition ? typeDefinition.custom_config_type.write(typeDefinition.custom_config_defaults) : {}],
    guid: uuidv4(),
    branches: [],
    winning_variant_idx: undefined,
  };
}

/**
 * Given a journey, and the type definition for an action, create an action of that type and add it to the journey
 * @param j
 * @param typeDefinition
 * @returns action that has been created.
 */
export function addAction<T_admin, T_public>(
  j: Journey,
  typeDefinition: null | ActionTypeDefinition<T_admin, T_public>
): Action {
  const a = createAction(typeDefinition);
  a.journey_guid = j.guid;
  j.actions.push(a);

  if (j.actions.length === 1) {
    j.initial_action_guid = a.guid;
  }

  return a;
}

export function addActionBranchAlways(a: Action, toGuid: ActionGuid) {
  a.branches.push({ target_guid: toGuid, comparison: undefined });
}

export function addActionBranchIf(a: Action, toGuid: ActionGuid, comparison: BranchComparison) {
  a.branches.push({ target_guid: toGuid, comparison: comparison });
}

/**
 * Find the action identified as the journeys initial action, or return undefined.
 */
export const journeyFindInitialAction = (j: Journey): Action | undefined => {
  if (j.initial_action_guid) {
    return maybeFindAction(j, j.initial_action_guid);
  } else {
    return undefined;
  }
};

/**
 * return action on journey matching guid, or undefined.
 */
export const maybeFindAction = (j: Journey, guid: ActionGuid): Action | undefined =>
  j.actions.find((a) => a.guid === guid);

/**
 * return action on journey matching guid, or throw a bug.
 */
export const mustFindAction = (j: Journey, guid: ActionGuid): Action =>
  maybeFindAction(j, guid) || bug(`mustFindAction: did not find action ${guid}`);

/**
 * @returns a new journey with action matching action.guid replaced (or added if not present)
 */
export const journeyUpdateActions = (journey: Journey, ...actions: Action[]): Journey => {
  const j: Journey = clone(journey);
  for (const updatedAction of actions) {
    j.actions = j.actions.filter((a) => a.guid !== updatedAction.guid);
    j.actions.push(updatedAction);
  }
  return j;
};

/**
 * Given a path, look up the JourneyContext for data that matches that path
 *  path is like: "member.address.line1"
 *   should look up: context['member']['address']['line'], while tolerating missing values
 */
export const lookupContextValue = (context: any, path: string): any => {
  return path.split(".").reduce((o, bit) => (o ? o[bit] : undefined), context);
};

async function doesConditionMatch(
  cond: BranchCondition,
  context: JourneyContext,
  branchInputDefs: Readonly<Map<string, ConditionInputDefinition>>
): Promise<boolean> {
  let branchArgumentValue = null;

  // condition has an argument. lookup its value using the argument source function
  if (cond.comparison) {
    const def = branchInputDefs.get(cond.comparison.input);
    if (!def) return error(`Unknown branch argument source type: ${cond.comparison.input}`);

    branchArgumentValue = await def.fn(cond.comparison, context);

    switch (cond.comparison.op) {
      case "truthy":
        return !!branchArgumentValue;
      case "falsey":
        return !branchArgumentValue;
      //case 'truthy': return !!lookupContextValue(context, cond.args.field);
      //case 'falsey': return !lookupContextValue(context, cond.args.field);
    }
  } else {
    // branch always
    return true;
  }
}

export function mergeContexts(old_context: JourneyContext, new_values: Partial<JourneyContext>): JourneyContext {
  return mergeDeepRight(old_context, new_values) as JourneyContext;
}

export async function findNextStep(
  journey: Journey,
  current_step: Action,
  context: JourneyContext,
  branchInputDefs: Readonly<Map<string, ConditionInputDefinition>>
): Promise<BranchCondition | "journey_ended"> {
  for (const branch of current_step.branches) {
    if (await doesConditionMatch(branch, context, branchInputDefs)) {
      return branch;
    }
  }
  return "journey_ended";
}

// returns [matching BranchCondition, context with newValues merged in]
export async function doActionTransition(
  journey: Journey,
  currentAction: Action,
  context: JourneyContext,
  newValues: JourneyContext,
  branchInputDefs: Readonly<Map<string, ConditionInputDefinition>>
): Promise<[BranchCondition, JourneyContext]> {
  const newContext = mergeContexts(context, newValues);
  const nextStep = await findNextStep(journey, currentAction, newContext, branchInputDefs);
  if (nextStep === "journey_ended") {
    return error("No step transition found. Journey may have ended");
  }
  return [nextStep, newContext];
}

export async function doAllConditionalTransitions(
  journey: Journey,
  currentAction: Action,
  context: JourneyContext,
  branchInputDefs: Readonly<Map<string, ConditionInputDefinition>>
): Promise<Action | "journey_ended"> {
  let counter = 0;
  while (currentAction.type == null) {
    // pure conditional action step. evaluate condition
    const branch = await findNextStep(journey, currentAction, context, branchInputDefs);
    if (branch === "journey_ended") {
      return "journey_ended";
    }
    currentAction = mustFindAction(journey, branch.target_guid);
    if (++counter > 100) {
      bug(`Got stuck in loop in startJourney. Is there a journey loop?`);
    }
  }
  return currentAction;
}

/**
 * Move an action step down the flow. This is only possible where the action has one child,
 * that also has only one child. ie you cannot use it on conditionals or steps leading to
 * conditionals.
 *
 * @returns an updated copy of the journey, or an error
 */
export function journeyMoveActionDown(j: Journey, actionGuid: ActionGuid): Journey | EditJourneyError {
  // validate that this action is possible
  const action = mustFindAction(j, actionGuid);
  if (action.branches.length === 0) return createError("Sorry we can't move this action - there is nothing after it!");

  if (action.branches.length !== 1)
    return createError("Sorry we can't move this action - it would confuse the branching");

  const child: Action = mustFindAction(j, action.branches[0].target_guid);
  if (child.branches.length > 1) return createError("Sorry we can't move this action - it would confuse the branching");

  return journeySwapActions(j, action.guid, child.guid);
}

export function journeyMoveActionUp(j: Journey, actionGuid: ActionGuid): Journey | EditJourneyError {
  // validate that this action is possible
  const _parent = j.actions.find((a) => a.branches.some((b) => b.target_guid === actionGuid));
  if (!_parent) return createError("Can't move this action up it's already at the top!");

  return journeyMoveActionDown(j, _parent.guid);
}

/**
 * TODO guard against swapping conditional and non-conditional branches.
 *
 * @returns a new Journey with the locations (in the flow-chart) of actions 1 & 2 swapped.
 */
export function journeySwapActions(j: Journey, actionGuid1: ActionGuid, actionGuid2: ActionGuid): Journey {
  // always acting on a clone, not mutating the argument
  const newJourney: Journey = clone(j);
  const action1 = mustFindAction(newJourney, actionGuid1);
  const action2 = mustFindAction(newJourney, actionGuid2);

  /* If one was the initial action step, swap this status */
  if (newJourney.initial_action_guid === action1.guid) {
    newJourney.initial_action_guid = action2.guid;
  } else if (newJourney.initial_action_guid === action2.guid) {
    newJourney.initial_action_guid = action1.guid;
  }

  {
    // swap their branch transitions
    const tmp = action1.branches;
    action1.branches = action2.branches;
    action2.branches = tmp;
  }

  // swap referenced target guids
  newJourney.actions = newJourney.actions.map((a) => ({
    ...a,
    branches: a.branches.map((b) => ({
      ...b,
      target_guid:
        b.target_guid !== action1.guid ? (b.target_guid === action2.guid ? action1.guid : b.target_guid) : action2.guid,
    })),
  }));

  return newJourney;
}

export function journeyDeleteAction(j: Journey, actionGuid: ActionGuid): Journey | EditJourneyError {
  const newJourney: Journey = clone(j);
  const action = mustFindAction(newJourney, actionGuid);

  if (action.branches.length === 0) {
    // delete branches going to the deleted action (since it's a terminus)
    newJourney.actions.forEach((a) => {
      a.branches = a.branches.filter((b) => b.target_guid !== actionGuid);
    });

    // if it was the initial action step, set to undefined (empty journey)
    if (newJourney.initial_action_guid === actionGuid) {
      newJourney.initial_action_guid = undefined;
    }
  }
  // only one unique branch target (though maybe multiple branches)
  else if (action.branches.length === 1 || uniq(action.branches.map((b) => b.target_guid)).length === 1) {
    // change branches going to the deleted action with branches going to its sole target
    newJourney.actions.forEach((a) => {
      a.branches.forEach((b) => {
        if (b.target_guid === actionGuid) {
          b.target_guid = action.branches[0].target_guid;
        }
      });
    });

    // if it was the initial action step, assign only child as initial action step
    if (newJourney.initial_action_guid === actionGuid) {
      newJourney.initial_action_guid = action.branches[0].target_guid;
    }
  } else {
    // this is the case of deleting a branching action - perhaps we should use noOrphans check
    // here instead
    return createError("Deleting this action will break the journey flow");
  }

  newJourney.actions = newJourney.actions.filter((a) => a.guid !== actionGuid);

  return newJourney;
}

/**
 * Return a new Journey where all the branches for action represented by actionGuid have been removed.
 * Return a EditJourneyError if the operation would break the journey flow.
 */
export function journeyDeleteBranches(j: Journey, actionGuid: ActionGuid): Journey | EditJourneyError {
  const newJourney: Journey = clone(j);
  const action = mustFindAction(newJourney, actionGuid);
  if (action.type == null) return createError("Can't end journey on a branch action");
  action.branches = [];

  if (hasOrphans(newJourney)) return createError("Deleting branches would create orphaned actions");
  else return newJourney;
}

/**
 *  A1 -> A2 ->A3
 *        + -> A4 -> A5
 * 
 * a1.branches = [A2]
 * a2.branches = [a3, a4]
 * a3.branches = []
 * a4.branches = [a5]
 * a5.branches = []
 * A2, A3, a4, a5 
 * 
 *    A1 -> A2 ->A3
 *        + -> A4         A5 <- orphan!

 * a1.branches = [A2]
 * a2.branches = [a3, a4]
 * a3.branches = []
 * a4.branches = []
 * a5.branches = []
 * All branches: A2, A3, A4 
 * @param j  
 */

function hasOrphans(j: Journey): boolean {
  if (j.actions.length === 0) return false; // empty journey has no orphans.

  // First get an array of all branch targets  in the journey - i.e. the guids of all actions that have a branch pointing at them.
  const allBranches = j.actions.reduce((all: ActionGuid[], action: Action) => {
    return all.concat(action.branches.map((b) => b.target_guid));
  }, []);

  const allActionGuids = j.actions.map((a) => a.guid);

  // if there are any actions in the journey that are not in all branches list, then they are orphans.
  // The initial action is allowed to be an orphan.
  const orphans = allActionGuids.filter((g) => !allBranches.includes(g));

  return !(orphans.length === 1 && orphans[0] === j.initial_action_guid);
}

export function journeyAddFirstAction(j: Readonly<Journey>, action: Action): Journey | EditJourneyError {
  if (j.actions.length > 0) return createError("Actions already exist in journey");

  const newJourney: Journey = clone(j);
  const newAction: Action = clone(action);
  newAction.journey_guid = newJourney.guid;
  newJourney.actions.push(newAction);
  newJourney.initial_action_guid = newAction.guid;
  return newJourney;
}

export function journeyInsertConditionUnder(
  j: Readonly<Journey>,
  parent_guid: ActionGuid,
  condition_internal_name: string,
  branch_idx: number // insert at this branch index
): Journey | EditJourneyError {
  // always acting on a clone, not mutating the argument
  const newJourney: Journey = clone(j);
  const newAction: Action = createAction(null); // no action type (member action): pure conditional action node
  newAction.journey_guid = newJourney.guid;

  const _parent = mustFindAction(newJourney, parent_guid);

  const branch: BranchCondition | undefined = _parent.branches[branch_idx];

  if (branch === undefined) return createError("Conditions can only be added above an existing action");

  newJourney.actions.push(newAction);

  // start with 'Yes' and 'No' condition branches both pointing to same target
  newAction.branches = [
    {
      target_guid: branch.target_guid,
      comparison: {
        op: "truthy",
        input: condition_internal_name,
        args: undefined,
      },
    },
    {
      target_guid: branch.target_guid,
      comparison: undefined, // 'No' is always-executed fallback
    },
  ];

  // link parent to new condition node
  branch.target_guid = newAction.guid;

  return newJourney;
}

/**
 * Given a action, a journey, and the GUID of an existing action in that journey, add the new action underneath the existing action.
 *
 * @param _j
 * @param parentGuid
 * @param _a
 * @param branch_idx
 * @returns modified journey, or 'NotAllowed'
 */
export function journeyInsertActionUnder(
  _j: Readonly<Journey>,
  parentGuid: ActionGuid,
  _a: Readonly<Action>,
  branch_idx: number
): Journey | EditJourneyError {
  // always acting on a clone, not mutating the argument
  const j: Journey = clone(_j);
  const a: Action = {
    ...clone(_a),
    journey_guid: j.guid,
  };
  const _parent = mustFindAction(j, parentGuid);

  const branch: BranchCondition | undefined = _parent.branches[branch_idx];

  j.actions.push(a);

  if (branch) {
    a.branches = [
      {
        target_guid: branch.target_guid,
        comparison: undefined, // branch always
      },
    ];

    _parent.branches[branch_idx].target_guid = a.guid;
  } else {
    _parent.branches.push({ target_guid: a.guid, comparison: undefined });
  }

  return j;
}

/**
 * @returns true if the journey contains no conditional branching (or loops - though they are forbidden)
 */
export function isJourneyLinear(journey: Journey): boolean {
  let a = journeyFindInitialAction(journey);

  // journeys with no initial step. This means either:
  // 1. either they journey is empty (no action steps)
  // 2. or the there is a loop (back-link to first step)
  //
  // Accept case (1), reject case (2)
  if (a === undefined) {
    return journey.actions.length === 0;
  }

  const visited = new Set([a.guid]);

  while (true) {
    // conditionals. not linear!
    if (a.branches.length > 1) return false;
    // end of journey
    else if (a.branches.length === 0) break;
    else {
      const target_guid = a.branches[0].target_guid;
      // loop detected!
      if (visited.has(target_guid)) return false;
      visited.add(target_guid);
      a = mustFindAction(journey, a.branches[0].target_guid);
    }
  }
  return true;
}

/**
 *
 * @param j Throw an error if j is considered invalid.
 */
export function validateJourney(j: Journey) {
  if (!j.initial_action_guid && j.actions.length > 0) error("No initial action and action length > 0");
  if (hasLoops(j)) error("Journey has cyclic loops.");
  if (hasOrphans(j)) error("Journey has unreachable actions.");
}

export function hasLoops(journey: Journey): boolean {
  if (!journey.initial_action_guid && journey.actions.length > 0) bug("No initial action and action length > 0");
  if (!journey.initial_action_guid) return false; // no actions, so no loops.

  const visited = new Set<string>();
  const stack = new Set<string>();

  function depthFirstSearch(currentGuid: string): boolean {
    visited.add(currentGuid);
    stack.add(currentGuid);
    const currentAction = maybeFindAction(journey, currentGuid);

    if (!currentAction) return false; // guid not found in journey.actions

    for (const branch of currentAction.branches) {
      if (!visited.has(branch.target_guid)) {
        if (depthFirstSearch(branch.target_guid)) return true;
      } else if (stack.has(branch.target_guid)) {
        return true; // A loop is detected.
      }
    }

    stack.delete(currentGuid);
    return false;
  }

  return depthFirstSearch(journey.initial_action_guid);
}

/**
 * Given a journey return a Graphviz representation which can be pasted into https://kroki.io/ or
 * used in CLI graphviz tools - gives you a quick visual for debugging.
 */
export function journeyToGraphviz(j: Journey) {
  let output = "digraph journey {\n";
  output =
    output +
    j.actions
      .map((a) => {
        const label = a.type ? a.type.split(":")[0] : "<>";
        return (
          `"${a.guid}" [label="${label}"]\n` +
          a.branches
            .map((b) => {
              return `"${a.guid}" -> "${b.target_guid}"\n`;
            })
            .join("\n")
        );
      })
      .join("\n");
  return output + "}\n";
}
